<template>
  <div class="card shadow-sm table-card mb-4">
    <div class="form-card-header flex-wrap">
      <div class="section-number"><i class="fe fe-activity" style="font-size: 12px;"></i></div>
      <div class="flex-grow-1">
        <h6 class="mb-0 font-weight-bold text-white">Forecast{{ data ? `: ${data.label}` : "" }}</h6>
        <small style="color: rgba(255,255,255,.75);">Next {{ horizon }} months from {{ data ? data.summary.months_of_history : "the" }} months of history{{ data ? ` · ${data.method}` : "" }}</small>
      </div>
      <select v-if="!fixedMetric" class="form-control form-control-sm" style="width: auto;" v-model="metric" @change="load" aria-label="Measure">
        <option v-for="(label, key) in metrics" :key="key" :value="key">{{ label }}</option>
      </select>
      <select class="form-control form-control-sm" style="width: auto;" v-model.number="horizon" @change="load" aria-label="Months ahead">
        <option :value="3">3 months</option><option :value="6">6 months</option><option :value="12">12 months</option>
      </select>
    </div>
    <div class="card-body">
      <div v-if="loading && !data" class="empty-state"><i class="mdi mdi-loading mdi-spin"></i>Loading…</div>
      <template v-else-if="data">
        <div class="d-flex flex-wrap mb-2" style="gap: 18px;">
          <div><span class="stat-label">Last 3 months</span><div class="stat-value">{{ fmt(data.summary.last_3_months) }}</div></div>
          <div><span class="stat-label">Next 3 months (forecast)</span><div class="stat-value">{{ fmt(data.summary.next_3_months) }}</div></div>
          <div v-if="data.summary.change_pct !== null"><span class="stat-label">Change</span>
            <div class="stat-value" :class="data.summary.change_pct >= 0 ? 'text-success' : 'text-danger'">{{ data.summary.change_pct >= 0 ? "▲" : "▼" }} {{ Math.abs(data.summary.change_pct) }}%</div></div>
          <div><span class="stat-label">Typical monthly error</span><div class="stat-value">± {{ fmt(data.summary.error) }}</div></div>
        </div>
        <apexchart type="line" :height="compact ? 240 : 320" :options="options" :series="series" :key="chartKey" />
        <p v-if="targetLines.length" class="small mb-1">
          <i class="fe fe-target mr-1 text-success"></i>Dashed green lines: new enrolments needed per month to meet
          <span v-for="(t, i) in targetLines" :key="t.id">{{ i ? ", " : "" }}"{{ t.name }}" ({{ fmt(t.required_per_month) }}/month)</span>.
          <router-link to="/forecasts?tab=targets">Targets</router-link>
        </p>
        <p class="small text-muted mb-0">The shaded band is a 95% range. Forecasts follow past patterns only; policy changes, outbreaks or enrolment drives are not foreseen.</p>
      </template>
    </div>
  </div>
</template>

<script>
/** Monthly forecast with a 95% band (GET analytics/forecast). */
export default {
  name: "ForecastPanel",
  props: {
    fixedMetric: { type: String, default: "" },
    facilityId: { type: [Number, String], default: null },
    compact: { type: Boolean, default: false },
  },
  data() {
    return {
      metric: this.fixedMetric || "encounters",
      horizon: 6,
      data: null,
      loading: false,
      chartKey: 0,
      targetLines: [],
      metrics: {
        enrollment: "New enrollees", encounters: "Encounters (utilization)", claims: "Claims submitted",
        claims_amount: "Amount claimed (₦)", authorization_codes: "Authorization codes",
      },
    };
  },
  computed: {
    series() {
      const h = this.data.history;
      const f = this.data.forecast;
      const pad = (n) => Array(n).fill(null);
      const last = h.length ? h[h.length - 1].value : null;
      return [
        { name: "Actual", data: h.map((p) => p.value).concat(pad(f.length)) },
        { name: "Forecast", data: pad(h.length - 1).concat([last]).concat(f.map((p) => p.value)) },
        { name: "High", data: pad(h.length).concat(f.map((p) => p.high)) },
        { name: "Low", data: pad(h.length).concat(f.map((p) => p.low)) },
      ];
    },
    options() {
      const cats = this.data.history.map((p) => p.label).concat(this.data.forecast.map((p) => p.label));
      return {
        chart: { toolbar: { show: !this.compact }, zoom: { enabled: false } },
        colors: ["#1e3a8a", "#7c3aed", "#c4b5fd", "#c4b5fd"],
        stroke: { width: [3, 3, 1, 1], dashArray: [0, 6, 3, 3], curve: "smooth" },
        xaxis: { categories: cats, labels: { rotate: -45, hideOverlappingLabels: true } },
        yaxis: { labels: { formatter: (v) => this.fmt(v) } },
        annotations: {
          yaxis: this.targetLines.map((t) => ({ y: t.required_per_month, borderColor: "#16a34a", strokeDashArray: 6,
            label: { text: `Needed for ${t.name}`, position: "left", textAnchor: "start", style: { background: "#16a34a", color: "#fff" } } })),
          xaxis: [{ x: this.data.forecast.length ? this.data.forecast[0].label : undefined, borderColor: "#94a3b8", label: { text: "Forecast", style: { background: "#7c3aed", color: "#fff" } } }] },
        dataLabels: { enabled: true, enabledOnSeries: [0, 1] },
        legend: { position: "top" },
        tooltip: { shared: true, intersect: false, y: { formatter: (v) => (v === null || v === undefined ? "—" : this.fmt(v)) } },
      };
    },
  },
  watch: { facilityId() { this.load(); } },
  mounted() { this.load(); },
  methods: {
    load() {
      this.loading = true;
      this.$batchGet("/api/v1/auth/analytics/forecast", { params: { metric: this.metric, horizon: this.horizon, facility_id: this.facilityId || undefined } })
        .then((res) => { this.data = res.data.data; this.chartKey++; })
        .then(() => this.loadTargets())
        .catch(() => {})
        .finally(() => { this.loading = false; });
    },
    // Running all-programme enrolment targets: the monthly pace each needs, drawn on the enrolment forecast.
    loadTargets() {
      if (this.metric !== "enrollment" || this.facilityId) { this.targetLines = []; return; }
      this.$batchGet("/api/v1/auth/targets", { params: { status: "active" } }).then((res) => {
        this.targetLines = (res.data.data || []).filter((t) => !t.sector && t.required_per_month).slice(0, 3);
        this.chartKey++;
      }).catch(() => { this.targetLines = []; });
    },
    fmt(v) {
      if (v === null || v === undefined) return "—";
      const n = Math.round(v);
      return this.metric === "claims_amount" ? "₦" + n.toLocaleString() : n.toLocaleString();
    },
  },
};
</script>

<style scoped>
@import "../../reports/reports.css";
</style>
